import { appConfig } from "@/config";
import { baseRefreshingQuery } from "@/core/api/baseQuery";
import { HttpMethod } from "@/types";
import { createApi } from "@reduxjs/toolkit/query/react";
import { Invitation, InviteUser, Organization, OrganizationMember, OrganizationMembersResponse, PreAuthToken, StoredFeatureFlags, UpdateMemberRole } from "@repo/schema";

const { organizations } = appConfig.studioApi.endpoints;

export enum OrganizationApiTagType {
  Organization = "Organization",
  OrganizationMembers = "OrganizationMembers",
  OrganizationInvitations = "OrganizationInvitations",
}

const organizationApiBase = createApi({
  reducerPath: "organizationApi",
  baseQuery: baseRefreshingQuery,
  tagTypes: Object.values(OrganizationApiTagType),
  endpoints: () => ({}),
});

export const organizationApi = organizationApiBase.injectEndpoints({
  endpoints: (builder) => ({
    getOrganization: builder.query<Organization, void>({
      query: () => ({
        url: organizations.current,
        method: HttpMethod.GET,
      }),
      providesTags: [OrganizationApiTagType.Organization],
    }),
    updateOrganization: builder.mutation<Organization, { name: string }>({
      query: (data) => ({
        url: organizations.current,
        method: HttpMethod.PATCH,
        body: data,
      }),
      invalidatesTags: [OrganizationApiTagType.Organization],
    }),
    /** Super-admin only. Replaces the whole blob — see featureFlags in @repo/schema. */
    updateFeatureFlags: builder.mutation<Organization, StoredFeatureFlags>({
      query: (featureFlags) => ({
        url: organizations.featureFlags,
        method: HttpMethod.PUT,
        body: { featureFlags },
      }),
      invalidatesTags: [OrganizationApiTagType.Organization],
    }),
    getMembers: builder.query<OrganizationMember[], void>({
      query: () => ({
        url: organizations.members,
        method: HttpMethod.GET,
      }),
      transformResponse: (response: OrganizationMembersResponse) =>
        response.members,
      providesTags: [OrganizationApiTagType.OrganizationMembers],
    }),
    inviteUser: builder.mutation<{ message: string }, InviteUser>({
      query: (data) => ({
        url: organizations.invite,
        method: HttpMethod.POST,
        body: data,
      }),
      invalidatesTags: [OrganizationApiTagType.OrganizationMembers, OrganizationApiTagType.OrganizationInvitations],
    }),
    getInvitations: builder.query<Invitation[], void>({
      query: () => ({
        url: organizations.invitations,
        method: HttpMethod.GET,
      }),
      providesTags: [OrganizationApiTagType.OrganizationInvitations],
    }),
    revokeInvitation: builder.mutation<{ message: string }, number>({
      query: (invitationId) => ({
        url: organizations.invitation(invitationId),
        method: HttpMethod.DELETE,
      }),
      invalidatesTags: [OrganizationApiTagType.OrganizationInvitations],
    }),
    removeMember: builder.mutation<{ message: string }, number>({
      query: (userId) => ({
        url: organizations.member(userId),
        method: HttpMethod.DELETE,
      }),
      invalidatesTags: [OrganizationApiTagType.OrganizationMembers],
    }),
    updateMemberRole: builder.mutation<{ message: string }, { userId: number } & UpdateMemberRole>({
      query: ({ userId, role }) => ({
        url: organizations.memberRole(userId),
        method: HttpMethod.PATCH,
        body: { role },
      }),
      invalidatesTags: [OrganizationApiTagType.OrganizationMembers],
    }),
    deleteOrganization: builder.mutation<PreAuthToken, void>({
      query: () => ({
        url: organizations.current,
        method: HttpMethod.DELETE,
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetOrganizationQuery,
  useUpdateOrganizationMutation,
  useUpdateFeatureFlagsMutation,
  useGetMembersQuery,
  useInviteUserMutation,
  useRemoveMemberMutation,
  useUpdateMemberRoleMutation,
  useGetInvitationsQuery,
  useRevokeInvitationMutation,
  useDeleteOrganizationMutation,
} = organizationApi;
